import { FC, useContext, useRef, useState } from 'react'
import { CutterContext } from '../contexts/CutterContext'
import { useDrag } from '../../hooks/useDrag'
import { InterfaceEvent } from '../../interfaces/InterfaceEvent'
import { PositionRange } from '../../types'
import { InternalContext } from '../contexts/InternalContext'
import { useDisableUserSelect } from '../../hooks/useDisableUserSelect'
import { useDocumentCursor } from '../../hooks/useDocumentCursor'
import { useAbsolutePositionPercent } from '../hooks/useAbsolutePositionPercent'
import './ScrollThumb.scss'
import { useMoveScroll } from './hooks/useMoveScroll'
import classNames from 'classnames'

interface Props {
  containerWidth: number
  className: string
}

export const ScrollThumb: FC<Props> = ({ className, containerWidth }) => {
  const { visibleRange } = useContext(CutterContext)
  const { setMouseCursorEnabled } = useContext(InternalContext)
  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const moveScroll = useMoveScroll(containerWidth)
  const left = useAbsolutePositionPercent(visibleRange[0])
  const width = useAbsolutePositionPercent(visibleRange[1]) - left
  const initialState = useRef<{ visibleRange: PositionRange; clientX: number }>()

  const drag = useDrag(element, {
    onDown({ clientX }: InterfaceEvent) {
      initialState.current = { clientX, visibleRange }
      setMouseCursorEnabled(false)
    },
    onMove(event: InterfaceEvent) {
      if (initialState.current) {
        moveScroll(event, initialState.current)
      }
    },
    onUp() {
      setMouseCursorEnabled(true)
    },
  })

  useDisableUserSelect(drag)
  useDocumentCursor('grabbing', drag)

  return (
    <div
      className={classNames('ScrollThumb', className, {
        '-grabbing': drag,
      })}
      style={{ left: `${left}%`, width: `${width}%` }}
      ref={setElement}
    >
      <span className="stroke" />
      <span className="stroke" />
      <span className="stroke" />
    </div>
  )
}
